import React, { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { dataHooks } from '../../dataHooks';
import { Box } from '@wix/design-system';
import { PasswordFieldWithLabel } from '../PasswordField/PasswordField';
import { EnterPasswordStore } from '../../stores/enterPassword';
import { ThemedTextButton } from '../ThemedComponents';
import { DialogFormContainer } from '../DialogContainer';
import { ButtonWithLoader } from '../ButtonWithLoader/ButtonWithLoader';
import {
  ACCOUNT_RECOVERY_V2_BASE_URL,
  ACCOUNT_RECOVERY_URL,
  buildAccountRecoveryV2Url,
} from '../../utils/constants';
import { EXPERIMENTS } from '../../stores/constants';
import { AppContextProvider } from '../AppLoader';
import { BodyContainer } from '../AuthPage/AuthPage';
import s from './EnterPassword.scss';
import classNames from 'classnames';

interface EnterPasswordProps {
  buttonContent?: string;
  dataHook?: string;
}

export const EnterPasswordContextProvider = createContext<{
  store?: EnterPasswordStore;
}>({});

export const EnterPassword: React.FC<EnterPasswordProps> = observer(
  ({
    children,
    buttonContent,
    dataHook = dataHooks.enterPassword.container,
  }) => {
    const { t } = useTranslation();
    const { store } = useContext(EnterPasswordContextProvider);
    const {
      rootStore: {
        displayStore: {
          isMobile,
          isVerticalLayout,
          authFormWidth,
          isWixel,
          preset,
        },
        experiments,
        navigationStore,
      },
    } = useContext(AppContextProvider);
    const overrideLocale = navigationStore.getQueryParam('overrideLocale');
    const forgotPasswordHref = experiments.enabled(
      EXPERIMENTS.NEW_ACCOUNT_RECOVERY_FLOW,
    )
      ? buildAccountRecoveryV2Url(ACCOUNT_RECOVERY_V2_BASE_URL, overrideLocale)
      : ACCOUNT_RECOVERY_URL;
    return (
      <DialogFormContainer
        dataHook={dataHook}
        formClasses={[
          s.enterPasswordContainer,
          isVerticalLayout ? s.vertical : '',
        ]}
      >
        <BodyContainer priority="secondary">
          {children}
          <Box
            direction="vertical"
            width={authFormWidth}
            maxWidth={isMobile ? '300px' : '320px'}
          >
            <PasswordFieldWithLabel
              required
              formField={store?.passwordField}
              label={t('account_verification.placeholder')}
              dataHook={dataHooks.enterPassword.password}
              autocomplete="current-password"
              showVisibility={!!store?.passwordField.value?.length}
              value={store?.passwordField.value}
              border={isVerticalLayout ? 'standard' : 'bottomLine'}
              onEnterPressed={() => store?.isSubmittable && store?.submit()}
            />
            <Box
              direction="vertical"
              gap={4}
              align={isVerticalLayout ? 'left' : 'center'}
            >
              <ThemedTextButton
                underline="always"
                size="small"
                as="a"
                href={forgotPasswordHref}
                target="_blank"
              >
                {t('forgot_password.link')}
              </ThemedTextButton>
              <ButtonWithLoader
                onClick={store?.submit}
                dataHook={dataHooks.enterPassword.submitButton}
                showArrowOnOver={
                  isVerticalLayout && preset.buttonDesign?.showArrowOnButton
                }
                skin="standard"
                type="submit"
                {...(isWixel && {
                  className: classNames(s.a11yFocus),
                })}
              >
                {buttonContent ?? t('account_verification.next')}
              </ButtonWithLoader>
            </Box>
          </Box>
        </BodyContainer>
      </DialogFormContainer>
    );
  },
);
